import { useEffect } from "react";

import TelemetryButton from "@/design-system/telemetry-button";
import { type ITelemetryCustom } from "@/lib/telemetry";
import type { ICashbackBannerElement } from "@/src/cashback/CashbackCommonInterfaces";
import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { useQueryPageCustom } from "@/src/query-client-factory";
import { useQueryClientCustom } from "@/src/query-client-overrides";
import PageLayout from "@/src/shared/page-layout";
import { FaqLandingPage } from "@Constants/PortalConstants";
import { CashbackDetails } from "@Routes/CashbackLinks";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, type QueryXsMax, colors } from "../../../styles/tokens.stylex";
import type { IContentContainer } from "../../common/ContentContainer";
import Help from "../../common/icons/Help";
import PanelSection from "../../shared/panel-section";
import BannerSection from "./BannerSection";
import type { IBrandsCashbackSectionResult } from "./BrandsCashbackSection";
import BrandsCashbackSection from "./BrandsCashbackSection";
import type { ICashbackDashboard } from "./CashbackDashboard";
import CashbackDashboard from "./CashbackDashboard";
import { CashbackDashboardProvider } from "./CashbackDashboardContext";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";
import { CashbackProcessSteps } from "./CashbackProcessSteps";

import * as css from "./CashbackMainPage.module.css";

export type ICashbackMainPage = IEntryPointComponent & {
    cashbackDashboard: ICashbackDashboard;
    bannerSection: ICashbackBannerElement[];
    popularCashbackBrands: IBrandsCashbackSectionResult;
    contentContainers: IContentContainer;
};

export function CashbackMainPage() {
    const queryClient = useQueryClientCustom();
    const { data } = useQueryPageCustom<ICashbackMainPage>({
        ignoreQuery: true,
        endpoint: CashbackDetails,
        queryKey: [CashbackDetails, "cashbackDashboard"],
        refetchOnWindowFocus: true,
    });
    const props = data?.component;

    const { isMobileOrTablet } = useViewportInfoContext();

    useEffect(
        () => () =>
            queryClient.removeQueries({
                queryKey: [CashbackDetails, "cashbackDashboard"],
            }),
        [queryClient],
    );

    if (!props) return null;

    const resources = languageSelector(CashbackMainPageResources, CashbackMainPageResourcesEn);

    const viewTelemetry = (event: string) =>
        ({ eventName: `cshbck-${event}`, actionType: "View" }) satisfies ITelemetryCustom;

    return (
        <PageLayout
            layoutAdUnitPath="cashback"
            slugTarget={CashbackDetails}
            breadcrumbs={[{ label: "Cashback" }]}
            isNewGrid
        >
            <div className={css.cashbackMainPageComponent}>
                <PanelSection
                    styleX={styles.linearBackground}
                    telemetryCustom={viewTelemetry("TopBanner")}
                    initialInView
                >
                    <CashbackProcessSteps key={isMobileOrTablet ? "mobile" : "desktop"} />
                </PanelSection>

                <PanelSection
                    className={css.headerBackground}
                    telemetryCustom={viewTelemetry("Dashboard")}
                    initialInView
                >
                    <>
                        {isMobileOrTablet && (
                            <div className={css.questionsContainer}>
                                <Help width={20} height={20} />
                                <TelemetryButton
                                    as="a"
                                    variant="transparent"
                                    size="None"
                                    styleX={styles.faqButton}
                                    eventName="cshbck-Faq"
                                    href={FaqLandingPage}
                                    isExternal
                                >
                                    {resources.transactionsManager.faqText}
                                </TelemetryButton>
                            </div>
                        )}
                        <CashbackDashboardProvider>
                            <CashbackDashboard {...props.cashbackDashboard} />
                        </CashbackDashboardProvider>
                    </>
                </PanelSection>

                {props.bannerSection?.length > 0 ? (
                    <PanelSection
                        styleX={styles.whiteBackground}
                        title={resources.bannerSectionTitle}
                        telemetryCustom={viewTelemetry("PromotionsBanner")}
                        hasLeftHeader
                        initialInView
                    >
                        <BannerSection initialData={props.bannerSection} />
                    </PanelSection>
                ) : null}

                <BrandsCashbackSection initialData={props.popularCashbackBrands} />
            </div>
        </PageLayout>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    faqButton: {
        margin: 0,
        padding: 0,
        fontWeight: "bold",
    },

    linearBackground: {
        padding: 0,
        backgroundPosition: "0% 0%",
        overflow: "unset",
        backgroundColor: "transparent",
        backgroundImage: `linear-gradient(9deg, ${colors.b_BrandNavy} 0%, ${colors.b_BrandMagenta} 100%)`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "padding-box",
        marginBottom: {
            default: "100px",
            [smMax]: "10px",
            [xsMax]: 0,
        },
    },

    whiteBackground: {
        paddingBlock: "50px",
        paddingInline: "0",
        backgroundColor: colors.bg_Primary,
        marginBottom: 0,
    },
});
